import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, PenLine, Loader2, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useUrlState } from "@/lib/useUrlState";

const STATUS = { submitted: ["Needs review", "bg-amber-100 text-amber-800"], reviewed: ["Awaiting parent signature", "bg-sky-100 text-sky-800"], signed: ["Signed", "bg-emerald-100 text-emerald-800"] };
const fmt = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function IncidentsPage() {
  const [filter, setFilter] = useUrlState("status", "");
  const [items, setItems] = useState(null);
  const [review, setReview] = useState(null); // incident being reviewed
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sig, setSig] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/daycare/incidents", { params: filter ? { status: filter } : {} });
    setItems(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submitReview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/daycare/incidents/${review.id}/review`, { admin_note: note, notify_parent: notify });
      toast.success(notify ? `Reviewed · parent emailed (${data.emails_sent})` : "Reviewed");
      setReview(null); setNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const showSig = async (inc) => { const { data } = await api.get(`/daycare/incidents/${inc.id}/signature`); setSig({ ...data, student_name: inc.student_name }); };

  return (
    <div className="p-6 lg:p-8" data-testid="incidents-page">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Daycare</div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Incident reports</h1>
          <p className="text-sm text-slate-500">Teachers file reports from the Daily Log. Review each one, then the parent signs it in the app.</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs">
          {[["", "All"], ["submitted", "Needs review"], ["reviewed", "Awaiting signature"], ["signed", "Signed"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`incident-filter-${v || "all"}`} className={`rounded-md px-3 py-1.5 ${filter === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
      </header>

      {!items ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div> : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center text-sm text-slate-400" data-testid="incidents-empty">No incident reports.</div>
      ) : (
        <div className="space-y-3">
          {items.map((i) => {
            const [label, cls] = STATUS[i.status] || STATUS.submitted;
            return (
              <div key={i.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`incident-row-${i.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-base font-semibold text-slate-900">{i.student_name} <span className="text-slate-400 font-normal">·</span> <span className="capitalize">{i.type.replace("_", " ")}</span></div>
                    <div className="text-xs text-slate-500">{fmt(i.occurred_at)}{i.location ? ` · ${i.location}` : ""} · reported by {i.reported_by_name}</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`} data-testid={`incident-status-${i.id}`}>{label}</span>
                </div>
                <p className="mt-3 text-sm text-slate-800">{i.description}</p>
                {(i.injury_area || i.first_aid) && <p className="mt-1 text-sm text-slate-600">{i.injury_area && <><b>Area:</b> {i.injury_area} </>}{i.first_aid && <><b>First aid:</b> {i.first_aid}</>}</p>}
                {i.admin_note && <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600"><b>Director:</b> {i.admin_note} — {i.reviewed_by_name}</p>}
                {i.parent_comment && <p className="mt-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-900"><b>Parent:</b> {i.parent_comment}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  {i.status === "submitted" && <button onClick={() => { setReview(i); setNote(""); setNotify(true); }} data-testid={`incident-review-${i.id}`} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white hover:shadow"><CheckCircle2 className="h-3.5 w-3.5" /> Review & send to parent</button>}
                  {i.status === "reviewed" && <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><PenLine className="h-3.5 w-3.5" /> Waiting for parent to sign in the app</span>}
                  {i.status === "signed" && <button onClick={() => showSig(i)} data-testid={`incident-signature-${i.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"><Eye className="h-3.5 w-3.5" /> Signed by {i.signer_name} · {fmt(i.signed_at)}</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {review && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setReview(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="incident-review-dialog">
            <div className="font-display text-lg font-semibold text-slate-900">Review report · {review.student_name}</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Director's note (optional, visible to parent)" data-testid="review-note"
              className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="review-notify" /> Email the parent to review & sign</label>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setReview(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">Cancel</button>
              <button onClick={submitReview} disabled={busy} data-testid="review-submit" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Mark reviewed</button>
            </div>
          </div>
        </div>
      )}
      {sig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setSig(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl text-center" data-testid="incident-signature-dialog">
            <div className="text-sm text-slate-500 mb-2">Signed by <b>{sig.signer_name}</b> · {fmt(sig.signed_at)}</div>
            <img src={sig.signature_data_url} alt="signature" className="mx-auto max-h-40 rounded-lg border border-slate-200 bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
